import { has } from 'lodash'
import { Card, Row, Col } from 'antd'
import moment from 'moment-timezone'
import ShipmentInfo from '@Orders/OrderDetailsPage/OrderDetailHeaderAndFooter/ShipmentInfo'
import {
    DELIVERY_STATUSES,
    DHL_EXPRESS_CARRIER_TYPE,
    DHL_EXPRESS_URL,
    FEDEX_CARRIER_TYPE,
    FEDEX_URL,
    SALES_ORDER_DETAILS,
    SHIPPING_METHOD_DISPLAY_NAMES,
    UPS_CARRIER_TYPE,
    UPS_URL,
} from '@Orders/constants'
import styles from '@Orders/OrderDetailsPage/styles.css'
import ShipTo from '@Orders/OrderDetailsPage/OrderDetailHeaderAndFooter/ShipTo'
import {
    salesOrder_salesOrder_shipments_items as ShipmentItemsType,
    salesOrder_salesOrder_paymentDetails_billingAddress as BillingAddressType,
    salesOrder_salesOrder_salesOrderLines_items as SalesOrderLineItemType,
} from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'

import Link from '~/core/Link'
import Box from '~/core/Box'

import ShippingStatus from './components/ShippingStatus'
import ShipmentDataTable from './components/ShipmentDataTable'
import { ShipmentHeader } from './components/ShipmentHeader'

type PropType = {
    id: string
    shipmentCount: number
    shipmentTotal: number
    shipmentData?: ShipmentItemsType
    deliveryAddress?: BillingAddressType | null
    isImOrder: boolean
    className?: string
    salesOrderLines?: SalesOrderLineItemType[]
    shipmentMethod?: string
}

const NOT_AVAILABLE = 'Not available'

export default function Shipment(props: PropType) {
    const {
        className,
        id,
        isImOrder,
        shipmentCount,
        shipmentTotal,
        shipmentData,
        salesOrderLines,
        shipmentMethod,
    } = props

    let trackingCodeUrl

    if (!shipmentData) {
        return ShipmentHeader(
            null,
            trackingCodeUrl,
            shipmentCount,
            shipmentTotal,
        )
    }

    const {
        carrier,
        trackingCode,
        trackingData,
        committedDeliveryDate,
        shipToAddress,
    } = shipmentData

    const shipmentArray = []

    shipmentArray.push(shipmentData)

    if (UPS_CARRIER_TYPE === carrier) {
        trackingCodeUrl = `${UPS_URL}${trackingCode}`
    } else if (FEDEX_CARRIER_TYPE === carrier) {
        trackingCodeUrl = `${FEDEX_URL}${trackingCode}`
    } else if (DHL_EXPRESS_CARRIER_TYPE === carrier) {
        trackingCodeUrl = `${DHL_EXPRESS_URL}${trackingCode}`
    }

    const lastStatus = trackingData?.trackingDetails.slice(-1)[0]

    const signedBy = trackingData?.signedBy || null

    let platformShipmentMethod = ''

    if (shipmentMethod && has(SHIPPING_METHOD_DISPLAY_NAMES, shipmentMethod)) {
        platformShipmentMethod =
            SHIPPING_METHOD_DISPLAY_NAMES[
                shipmentMethod as keyof typeof SHIPPING_METHOD_DISPLAY_NAMES
            ]
    }
    const trackingStatus =
        trackingData?.status === DELIVERY_STATUSES.DELIVERED
            ? 'Delivered'
            : 'Estimated delivery'

    return (
        <Card
            className={className}
            style={{
                marginBottom: '16px',
                borderRadius: '4px',
            }}
        >
            <Row style={{ marginBottom: '8px' }}>
                <Col>
                    <ShipmentInfo title="">
                        {ShipmentHeader(
                            trackingData,
                            trackingCodeUrl,
                            shipmentCount,
                            shipmentTotal,
                        )}
                    </ShipmentInfo>
                </Col>
            </Row>
            <Box marginBottom="20px">
                <Box direction="col" width="320px" flexShrink="0">
                    <ShipmentInfo title={trackingStatus}>
                        <Box>
                            <Box className={styles.titleTextHeader}>
                                {moment(committedDeliveryDate).format(
                                    'DD MMM YYYY',
                                )}
                            </Box>
                        </Box>
                    </ShipmentInfo>
                    <ShipmentInfo title="Tracking number">
                        {trackingCodeUrl && trackingCode ? (
                            <Link
                                placement={SALES_ORDER_DETAILS}
                                name="tracking-code"
                                href={trackingCodeUrl}
                                target="_blank"
                                data-test-target="tracking-code-link"
                            >
                                {trackingCode}
                            </Link>
                        ) : (
                            NOT_AVAILABLE
                        )}
                    </ShipmentInfo>
                </Box>
                <Box
                    direction="col"
                    width="238px"
                    marginLeft="10px"
                    marginRight="10px"
                >
                    <ShipmentInfo title="Ship to">
                        <Box marginTop="5px">
                            <ShipTo postalAddress={shipToAddress || null} />
                        </Box>
                    </ShipmentInfo>
                </Box>
                <Box direction="col" width="238px">
                    <ShipmentInfo title="Shipping method">
                        <Box marginTop="5px">
                            {platformShipmentMethod
                                ? `${platformShipmentMethod}`
                                : NOT_AVAILABLE}
                        </Box>
                    </ShipmentInfo>
                </Box>
                <Box direction="col" width="238px" marginLeft="10px">
                    <ShipmentInfo title="Shipping status">
                        {lastStatus ? (
                            <ShippingStatus
                                trackingDetails={lastStatus}
                                carrier={carrier}
                                signedBy={signedBy}
                            />
                        ) : (
                            NOT_AVAILABLE
                        )}
                    </ShipmentInfo>
                </Box>
            </Box>
            <Row style={{ display: 'block' }}>
                <Col>
                    <ShipmentDataTable
                        shipmentId={id}
                        isImOrder={isImOrder}
                        salesOrderLines={salesOrderLines || []}
                        shipmentDetail={shipmentArray}
                    />
                </Col>
            </Row>
        </Card>
    )
}
